import { CircularProgress } from '@material-ui/core';
import clsx from 'clsx';
import { RELOAD_TIME_DATA_QUALITY } from 'constants/intervals';
import { computeDataQualityLight } from 'helpers/DataQualityHelper';
import { getFlowoutsNA } from 'helpers/getFlowoutsNa';
import { useIntervalWhenWindowIsFocused } from 'hooks/useIntervalWhenWindowIsFocused';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import {
  useGetDataConsistencyByWellbore,
  useGetDataQualityByWellbore,
  useGetOutliersByWellbore,
} from 'services/networking/DataQualityApiHooks';
import { useFetchWellboreMnemonicsMapping } from 'services/networking/KickStuckApiHooks';
import { getErrorMessage } from 'services/networking/errorHandler';
import { InnerSensorWellboreWithWellInfo } from 'types/wellbore';

import styles from './HeaderDataQuality.module.scss';
import { DataQualityIndicatorColor } from './components/WellLiveDataQualityIndicator';
import WellboreDataQualityPopin from './components/WellboreDataQualityPopin';
import { computeDataConsistencyIndex } from '../../../../pages/DataManagementDataQuality/components/ClosedRow/utils/dataConsistencyIndex';
import { getAllMnemonicsRatioData } from '../../../../pages/DataManagementDataQuality/components/ClosedRow/utils/mnemonicsRatioData';

interface Props {
  locationType?: string;
  wellbore: InnerSensorWellboreWithWellInfo;
}

const HeaderDataQuality = ({ wellbore, locationType }: Props) => {
  const intl = useIntl();
  const [isOpen, setOpen] = useState(false);
  const {
    dataHoleError,
    dataHole: dataIntermittence,
    isLoadingDataHole,
    doFetchDataHoleByWellbore,
  } = useGetDataQualityByWellbore(wellbore.wellboreId);
  const { outlierError, outlier, isLoadingOutlier, doFetchOutliersByWellbore } =
    useGetOutliersByWellbore(wellbore.wellboreId);
  const {
    responseBody: wellboreMnemonicsMapping,
    isLoading: allMnemonicsMappingLoading,
    doFetch: doFetchWellboreMnemonicsMapping,
  } = useFetchWellboreMnemonicsMapping(wellbore.wellboreId);

  const {
    dataConsistencyError,
    dataConsistency,
    isLoadingDataConsistency,
    doFetchDataConsistencyByWellbore,
  } = useGetDataConsistencyByWellbore(wellbore.wellboreId);
  const wellboreDataQualityLoading =
    isLoadingDataHole || isLoadingDataConsistency || isLoadingOutlier || allMnemonicsMappingLoading;
  const error = { ...dataHoleError, ...outlierError, ...dataConsistencyError };

  if (error.message) {
    toast.error(getErrorMessage(error));
  }

  const fetchData = useCallback(() => {
    doFetchDataHoleByWellbore();
    doFetchOutliersByWellbore();
    doFetchWellboreMnemonicsMapping();
    doFetchDataConsistencyByWellbore();
  }, [
    doFetchDataHoleByWellbore,
    doFetchOutliersByWellbore,
    doFetchWellboreMnemonicsMapping,
    doFetchDataConsistencyByWellbore,
  ]);

  useEffect(() => {
    fetchData();
  }, [wellbore, fetchData]);

  useIntervalWhenWindowIsFocused(() => {
    fetchData();
  }, RELOAD_TIME_DATA_QUALITY);

  const { isFlowOutAVNA, isFlowOutPCNA, isFlowInBstNA } = getFlowoutsNA(
    wellbore.well?.locationType,
    wellboreMnemonicsMapping,
  );
  const mnemonicsRatioData = getAllMnemonicsRatioData(
    isFlowInBstNA,
    isFlowOutAVNA,
    isFlowOutPCNA,
    wellboreMnemonicsMapping,
  );
  const dataConsistencyIndex = computeDataConsistencyIndex(
    isFlowInBstNA,
    dataConsistency,
    wellboreMnemonicsMapping,
  );

  const isDataQualityAvailable =
    outlier && dataConsistencyIndex && dataIntermittence && !error.response;

  const { isGreenOn, isOrangeOn, isRedOn, dataQualityIndex } = computeDataQualityLight({
    outlier,
    dataIntermittence,
    mnemonicsRatioData,
    dataConsistencyIndex,
  });

  const dataQualityIndicatorColor =
    (isGreenOn && DataQualityIndicatorColor.HIGH) ||
    (isOrangeOn && DataQualityIndicatorColor.MEDIUM) ||
    ((isRedOn && DataQualityIndicatorColor.LOW) as DataQualityIndicatorColor);

  const toggleWellboreDataQualityPopin = () => setOpen(prevState => !prevState);

  return (
    <>
      {wellboreDataQualityLoading ? (
        <CircularProgress size={24} className={styles.loader} />
      ) : (
        <div className={styles.headerDataQuality}>
          <span className={styles.liveDataQualityText}>
            {intl.formatMessage({ id: `header.liveDataQuality` })}
          </span>
          <div
            data-testid="livedataQuality"
            className={styles.dataQualityContainer}
            onClick={toggleWellboreDataQualityPopin}
          >
            {isDataQualityAvailable && (
              <div
                className={clsx(
                  { [styles.green]: isGreenOn, [styles.orange]: isOrangeOn, [styles.red]: isRedOn },
                  styles.lightIndicator,
                )}
              />
            )}
            <span className={styles.viewMoreDataText}>
              {intl.formatMessage({ id: `header.fullReport` })}
            </span>
          </div>
        </div>
      )}
      <WellboreDataQualityPopin
        open={isOpen}
        wellbore={wellbore}
        outlier={outlier}
        locationType={locationType}
        isFlowInBstNA={isFlowInBstNA}
        isFlowOutAVNA={isFlowOutAVNA}
        isFlowOutPCNA={isFlowOutPCNA}
        dataConsistency={dataConsistency}
        mnemonicsMapping={wellboreMnemonicsMapping}
        dataIntermittence={dataIntermittence}
        onClose={toggleWellboreDataQualityPopin}
        wellboreDataQualityColor={dataQualityIndicatorColor}
        wellboreDataQualityIndex={dataQualityIndex}
      />
    </>
  );
};

export default HeaderDataQuality;
